import axiosInstance from "../utils/axios";

/**
 * Requests a Power BI embed preview for a report URL or id.
 * @param {Object} values - Preview payload (`url` or `id`).
 * @param {import("axios").CancelToken} [cancelToken] - Optional cancel token.
 * @returns {Promise<import("axios").AxiosResponse>} Preview API response.
 */
export const previewReportCancelToken = async (values, cancelToken) => {
  return axiosInstance().post("/previewReport", values, { cancelToken });
};

/**
 * Creates or updates a report record.
 * @param {Object} values - Report form payload.
 * @param {import("axios").CancelToken} [cancelToken] - Optional cancel token.
 * @returns {Promise<import("axios").AxiosResponse>} Save API response.
 */
export const addAndUpdateReportCancelToken = async (values, cancelToken) => {
  return axiosInstance().post("/addAndUpdateReport", values, { cancelToken });
};

/**
 * Fetches report category options for form dropdowns.
 * @param {Object} [params] - Optional query params.
 * @param {import("axios").CancelToken} [cancelToken] - Optional cancel token.
 * @returns {Promise<import("axios").AxiosResponse>} Categories API response.
 */
export const getReportCategories = async (params, cancelToken) => {
  return axiosInstance().get("/getReportCategories", { params, cancelToken });
};

/**
 * Fetches a single report by id.
 * @param {Object} params - Query params including report id.
 * @param {import("axios").CancelToken} [cancelToken] - Optional cancel token.
 * @returns {Promise<import("axios").AxiosResponse>} Report details API response.
 */
export const getReportDetails = async (params, cancelToken) => {
  return axiosInstance().get("/getReportDetails", { params, cancelToken });
};

/**
 * Fetches paginated or export report list data.
 * @param {Object} params - List query params.
 * @param {import("axios").CancelToken} [cancelToken] - Optional cancel token.
 * @returns {Promise<import("axios").AxiosResponse>} Report list API response.
 */
export const getReportList = async (params, cancelToken) => {
  return axiosInstance().get("/getReportList", { params, cancelToken });
};

/**
 * Fetches filter dropdown options for the reports list.
 * @param {Object} [params] - Optional query params.
 * @param {import("axios").CancelToken} [cancelToken] - Optional cancel token.
 * @returns {Promise<import("axios").AxiosResponse>} Filter options API response.
 */
export const getReportListFilter = async (params, cancelToken) => {
  return axiosInstance().get("/getReportListFilter", { params, cancelToken });
};

/**
 * Deletes a report by id.
 * @param {Object} values - Delete payload including report id.
 * @param {import("axios").CancelToken} [cancelToken] - Optional cancel token.
 * @returns {Promise<import("axios").AxiosResponse>} Delete API response.
 */
export const deleteReport = async (values, cancelToken) => {
  return axiosInstance().post("/deleteReport", values, { cancelToken });
};

/**
 * Fetches audit trail filter dropdown values for reports.
 * @param {Object} [params] - Optional query params.
 * @param {import("axios").CancelToken} [cancelToken] - Optional cancel token.
 * @returns {Promise<import("axios").AxiosResponse>} Audit trail filter API response.
 */
export const getReportsAuditTrailDropdown = async (params, cancelToken) => {
  return axiosInstance().get("/getReportsAuditTrailDropdown", {
    params,
    cancelToken,
  });
};

/**
 * Fetches paginated reports audit trail records.
 * @param {Object} values - Audit trail query payload.
 * @param {import("axios").CancelToken} [cancelToken] - Optional cancel token.
 * @returns {Promise<import("axios").AxiosResponse>} Audit trail API response.
 */
export const getReportsAuditTrail = async (values, cancelToken) => {
  return axiosInstance().post("/getReportsAuditTrail", values, { cancelToken });
};
